<template>
  <jz-container id="course-catalog-container" flex column fill>
    <jz-spinner v-if="loading" />

    <jz-card v-else class="course-catalog-card">
      <template #header>
        <jz-title>
          <jz-icon :src="booksIcon" />

          {{ "course.explore.title" | __ }}

          <template v-slot:right-side>
            <div class="filter-toggle">
              <jz-input
                class="search-field"
                :placeholder="'course.explore.search.placeholder' | __"
                type="text"
                :value="searchString"
                @input="val => {}"
                @input-backward-compatible="handleCourseCatalogSearchInput"
              />

              <span
                :class="['filter-button', 'search', { open: searchIsShowing }]"
                @click="toggleSearch"
              >
                <jz-icon :src="magnifyingGlassIcon" />
              </span>

              <span :class="['filter-button', { open: filtersAreShowing }]" @click="toggleFilters">
                <jz-container v-if="totalFilterCount" class="total-filter-count">{{
                  totalFilterCount
                }}</jz-container>

                <jz-icon :src="filterIcon" />
              </span>
            </div>
          </template>
        </jz-title>
      </template>
      <jz-container class="filters" :class="{ hidden: !searchIsShowing }">
        <jz-input
          class="search-field"
          :placeholder="'course.explore.search.placeholder' | __"
          type="text"
          :value="searchString"
          @input="val => {}"
          @input-backward-compatible="handleCourseCatalogSearchInput"
        />
      </jz-container>
      <jz-container class="filters" :class="{ hidden: !filtersAreShowing }">
        <jz-tabs>
          <jz-tab :title="'course.explore.filter.level' | __" :count="selectedLevelIdCount">
            <keep-alive>
              <level-filter
                :cefrj-level-counts="cefrjLevelCounts"
                :exam-selection-handler="handleExamSelection"
                :level-selection-handler="handleLevelSelection"
                :selected-exam-index="selectedExamIndex"
                :selected-level-indexes="selectedLevelIndexes"
              />
            </keep-alive>
          </jz-tab>

          <jz-tab :title="'course.explore.filter.category' | __" :count="selectedCategoryIdCount">
            <keep-alive>
              <category-filter
                :categories="courseCategories"
                :selected-category-ids="selectedCategoryIds"
                :category-selection-handler="handleCategorySelection"
              />
            </keep-alive>
          </jz-tab>
        </jz-tabs>
      </jz-container>

      <jz-container class="courses">
        <router-link
          v-for="course in filteredOrganizationCourses"
          :key="course.uuid"
          :to="{ name: courseDetailRouteName, params: { courseUuid: course.uuid } }"
        >
          <jz-card class="explorer-course-card">
            <course-list-item :course="course" />
          </jz-card>
        </router-link>
      </jz-container>
    </jz-card>
  </jz-container>
</template>

<script>
import { mapState, mapGetters } from "vuex";
import {
  ROUTES,
  VUEX_CONTENT_ACTION_NAMES,
  VUEX_CONTENT_NAMESPACE,
  VUEX_ROOT_MUTATION_NAMES
} from "@tt-app/constants";
import CourseListItem from "@tt-app/components/course/CourseListItem.vue";
import LevelFilter from "@tt-app/components/course/catalog/LevelFilter.vue";
import CategoryFilter from "@tt-app/components/course/catalog/CategoryFilter.vue";

import booksIcon from "@joyz/app-core/assets/icons/default/Books.svg";
import filterIcon from "@joyz/app-core/assets/icons/default/Filter.svg";
import magnifyingGlassIcon from "@joyz/app-core/assets/icons/default/MagnifyingGlass.svg";

const { COURSE } = ROUTES;

export default {
  name: "CourseExplorer",
  components: {
    CategoryFilter,
    LevelFilter,
    CourseListItem
  },
  data() {
    return {
      loading: false,
      filtersAreShowing: false,
      searchIsShowing: false,
      searchString: "",
      selectedExamIndex: "0",
      selectedLevelIndexes: {},
      selectedCategoryIds: {}
    };
  },
  computed: {
    ...mapState({
      courseCategories: state => state.content.organization.categories,
      cefrjLevelCounts: state => state.content.organization.cefrjLevelCounts
    }),
    ...mapGetters({
      filteredOrganizationCourses: "content/filteredOrganizationCourses",
      storedCategoryIds: "course_explore_selected_category_ids",
      storedExamIndex: "course_explore_selected_exam_index",
      storedLevelIndexes: "course_explore_selected_level_indexes",
      storedSearchString: "course_explore_search_string"
    }),
    selectedCategoryIdCount() {
      return Object.values(this.selectedCategoryIds).filter(Boolean).length || undefined;
    },
    selectedLevelIdCount() {
      return Object.values(this.selectedLevelIndexes).filter(Boolean).length || undefined;
    },
    totalFilterCount() {
      const levelCount = this.selectedLevelIdCount || 0;
      const categoryCount = this.selectedCategoryIdCount || 0;
      return levelCount + categoryCount;
    }
  },
  watch: {
    searchString: {
      handler(newSearchString) {
        this.$store.commit(
          `${VUEX_ROOT_MUTATION_NAMES.COURSE_EXPLORE_SEARCH_STRING}`,
          newSearchString
        );
      },
      immediate: false
    },
    selectedCategoryIds: {
      handler(newCategoryIds) {
        this.$store.commit(
          `${VUEX_ROOT_MUTATION_NAMES.COURSE_EXPLORE_SELECTED_CATEGORY_IDS}`,
          newCategoryIds
        );
      },
      immediate: false
    },
    selectedExamIndex: {
      handler(newExamIndex) {
        this.$store.commit(
          `${VUEX_ROOT_MUTATION_NAMES.COURSE_EXPLORE_SELECTED_EXAM_INDEX}`,
          parseInt(newExamIndex, 10)
        );
      },
      immediate: false
    },
    selectedLevelIndexes: {
      handler(newLevelIndexes) {
        this.$store.commit(
          `${VUEX_ROOT_MUTATION_NAMES.COURSE_EXPLORE_SELECTED_LEVEL_INDEXES}`,
          newLevelIndexes
        );
      },
      immediate: false
    }
  },
  created() {
    this.fetchInitialData();
    this.courseDetailRouteName = COURSE.DETAILS.NAME;
    this.filterIcon = filterIcon;
    this.booksIcon = booksIcon;
    this.magnifyingGlassIcon = magnifyingGlassIcon;

    this.searchString = this.storedSearchString;
    this.selectedCategoryIds = this.storedCategoryIds;
    this.selectedExamIndex = this.storedExamIndex;
    this.selectedLevelIndexes = this.storedLevelIndexes;
  },
  methods: {
    async fetchInitialData() {
      this.loading = true;
      await this.$store.dispatch(
        `${VUEX_CONTENT_NAMESPACE}/${VUEX_CONTENT_ACTION_NAMES.ORG_COURSES_REQUEST}`
      );
      this.loading = false;
    },
    handleCategorySelection(event) {
      const {
        currentTarget: { dataset }
      } = event;

      if (!dataset.uuid) {
        this.selectedCategoryIds = {};
        return;
      }

      const { uuid } = dataset;

      const previousCategories = this.selectedCategoryIds;
      const previousValue = previousCategories[uuid] || false;

      this.selectedCategoryIds = {
        ...previousCategories,
        [uuid]: !previousValue
      };
    },
    handleCourseCatalogSearchInput(event) {
      const {
        currentTarget: { value }
      } = event;

      this.searchString = value;
    },
    handleExamSelection(examIndex) {
      this.selectedLevelIndexes = {};
      this.selectedExamIndex = examIndex;
    },
    handleLevelSelection(event) {
      const {
        currentTarget: { dataset }
      } = event;

      if (!dataset.levelIndex) {
        this.selectedLevelIndexes = {};
        return;
      }

      const { levelIndex } = dataset;

      const previousValue = this.selectedLevelIndexes[levelIndex] || false;

      this.selectedLevelIndexes = { ...this.selectedLevelIndexes, [levelIndex]: !previousValue };
    },
    toggleFilters() {
      this.searchIsShowing = false;
      this.filtersAreShowing = !this.filtersAreShowing;
    },
    toggleSearch() {
      this.filtersAreShowing = false;
      this.searchIsShowing = !this.searchIsShowing;
    }
  }
};
</script>

<style lang="scss">
#course-catalog-container {
  height: 100%;
  overflow: auto;
  padding: ($gutter / 2) ($gutter / 2) 0 ($gutter / 2);

  .course-catalog-card {
    border-bottom-left-radius: 0;
    border-bottom-right-radius: 0;
    display: block;
    max-width: 920px;

    .jz-card-header {
      .filter-toggle {
        align-items: center;
        display: flex;
        flex-direction: row;
        position: relative;

        .jz-input {
          display: none;
          height: 40px;
          margin: 0 $gutter 0 0;

          // TODO: to remove if JzInput component handles height
          input {
            height: 40px;
          }
        }

        .filter-button {
          align-items: center;
          border-radius: 20px;
          display: flex;
          flex-direction: row;
          font-size: $font-size-xs;
          margin-right: $gutter / 2;
          padding: $gutter / 2;

          &:last-child {
            margin-right: $gutter / 2;
          }

          &:hover {
            background-color: var(--color-primary-alternate-background-light);
            cursor: pointer;
          }

          &.open {
            background-color: var(--color-primary-alternate-background-light);
          }

          .total-filter-count {
            color: var(--color-dark);
            font-weight: bold;
            font-size: $font-size-sm;
            margin: 0 ($gutter / 4);
          }
        }
      }
    }

    .filters {
      background-color: var(--color-primary-alternate-background-light);
      border-bottom: 1px solid rgba(var(--rgb-black), 0.08);
      padding: $gutter;

      &.hidden {
        display: none;
      }

      .jz-tabs {
        padding: 0;
      }
    }

    .courses {
      display: flex;
      flex-wrap: wrap;
      padding: $gutter / 2;

      a {
        color: unset;
        flex-basis: 100%;
        flex-grow: 0;
        margin: $gutter / 2;
        text-decoration: unset;
      }
    }
  }
}

@include responsive(tablet) {
  #course-catalog-container {
    .course-catalog-card {
      .jz-card-header {
        .filter-toggle {
          .jz-input {
            display: block;
          }

          .filter-button {
            &.search {
              display: none;
            }
          }
        }
      }

      .courses {
        a {
          flex-basis: calc(50% - 16px);
        }
      }
    }
  }
}

@include responsive(desktop) {
  #course-catalog-container {
    .course-catalog-card {
      display: flex;
      overflow: hidden;

      .courses {
        overflow: auto;
      }
    }
  }
}
</style>
